import * as SignalR from '@microsoft/signalr';
import * as React from 'react';
import { useCallback } from 'react';
import { websocketRetryPolicy } from '../websocketRetryPolicy.js';
export const START_WS_RETRY_DELAY = 5000;
// This hook is used to create a SignalR connection and manage its lifecycle
// It provides auto /manual connect feature with retry mechanism
// It can be used with a context to keep a unique connection between multiple hook calls
const useSignalR = ({ url, contextConnection, autoConnect = true, maxConnectAttempts = 0, // 0 means infinite
 }) => {
    if (!url)
        throw new Error('URL is required');
    const [connection, setConnection] = React.useState(contextConnection || undefined);
    React.useEffect(() => {
        if (connection)
            return;
        const newConnection = window.signalRMock ||
            new SignalR.HubConnectionBuilder()
                .withUrl(url, {
                transport: SignalR.HttpTransportType.WebSockets,
            })
                .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: websocketRetryPolicy,
            })
                .build();
        setConnection(newConnection);
    }, [connection, url]);
    const [hasAutoStarted, setHasAutoStarted] = React.useState(false);
    const [state, setState] = React.useState((contextConnection === null || contextConnection === void 0 ? void 0 : contextConnection.state) || SignalR.HubConnectionState.Disconnected);
    const onclose = (error) => {
        setState(SignalR.HubConnectionState.Disconnected);
        if (error)
            console.warn('Websocket connection closed', error.message, error.stack);
    };
    const onreconnecting = useCallback((error) => {
        setState((connection === null || connection === void 0 ? void 0 : connection.state) || SignalR.HubConnectionState.Reconnecting);
        if (error) {
            console.warn('Websocket connection stopped, reconnecting...', error.message, error.stack);
        }
    }, [connection]);
    const onreconnected = useCallback(() => {
        setState((connection === null || connection === void 0 ? void 0 : connection.state) || SignalR.HubConnectionState.Connected);
        console.warn('Websocket connection reconnected', undefined, undefined);
    }, [connection]);
    // Register and cleanup connection lifecycle events
    React.useEffect(() => {
        if (connection) {
            connection.onclose(onclose);
            connection.onreconnecting(onreconnecting);
            connection.onreconnected(onreconnected);
        }
    }, [connection, onreconnected, onreconnecting]);
    // Start connection handler with auto-retry
    const startConnection = React.useCallback((attempt = 1) => {
        if (!connection)
            return Promise.reject(new Error("Can't start connection, connection is not defined"));
        if (connection.state !== SignalR.HubConnectionState.Disconnected)
            return Promise.reject(new Error(`Can't start connection, the connection state is not disconnected: ${connection.state}`));
        return connection
            .start()
            .then(() => {
            setState(SignalR.HubConnectionState.Connected);
        })
            .catch(err => {
            console.error(`Unable to establish a WS connection with ${url} (attempt #${attempt}).`, err, err.stack);
            setState(SignalR.HubConnectionState.Disconnected);
            if (maxConnectAttempts === 0 || attempt < maxConnectAttempts) {
                setTimeout(() => startConnection(attempt + 1), websocketRetryPolicy({ previousRetryCount: attempt - 1 }));
            }
        });
    }, [connection, maxConnectAttempts, url]);
    // Auto-start connection when autoConnect is enabled
    React.useEffect(() => {
        if (connection &&
            autoConnect &&
            !hasAutoStarted &&
            state === SignalR.HubConnectionState.Disconnected) {
            setHasAutoStarted(true);
            startConnection();
        }
    }, [autoConnect, connection, hasAutoStarted, startConnection, state]);
    return {
        connection,
        connectionState: state,
        start: startConnection,
        stop: connection === null || connection === void 0 ? void 0 : connection.stop.bind(connection),
        on: connection === null || connection === void 0 ? void 0 : connection.on.bind(connection),
        off: connection === null || connection === void 0 ? void 0 : connection.off.bind(connection),
        invoke: connection === null || connection === void 0 ? void 0 : connection.invoke.bind(connection),
    };
};
export default useSignalR;
//# sourceMappingURL=useSignalR.js.map